// Download utility functions for charts and data
import type { RefObject } from 'react';
import { toPng } from 'html-to-image';

/** Custom event name for "download all chart PNGs" from the header modal. Charts that support PNG export listen and trigger their export. */
export const DOWNLOAD_ALL_PNGS_EVENT = 'climateplans-download-all-pngs';

/** Dispatches an event that all chart layouts with PNG export will handle by triggering their download. */
export const triggerDownloadAllPNGs = () => {
  window.dispatchEvent(new CustomEvent(DOWNLOAD_ALL_PNGS_EVENT));
};

export const downloadChartAsPNG = (chartRef: RefObject<HTMLCanvasElement | null>, filename: string) => {
  if (!chartRef.current) {
    console.error('Chart ref not available');
    return;
  }

  const canvas = chartRef.current;
  const link = document.createElement('a');
  link.download = `${filename}.png`;
  link.href = canvas.toDataURL('image/png');
  link.click();
};

/** react-chartjs-2 / Chart.js v4 chart instance exposes `.canvas`. */
export function downloadChartJsInstanceAsPNG(
  chart: { canvas?: HTMLCanvasElement } | null | undefined,
  filename: string
): void {
  const canvas = chart?.canvas;
  if (!canvas) {
    console.error('Chart canvas not available');
    return;
  }
  const link = document.createElement('a');
  link.download = `${filename}.png`;
  link.href = canvas.toDataURL('image/png');
  link.click();
}

/** Rasterize a DOM subtree (Recharts, Leaflet, multi-panel figures). */
export async function downloadDomAsPNG(element: HTMLElement | null | undefined, filename: string): Promise<void> {
  if (!element) {
    console.error('downloadDomAsPNG: element missing');
    return;
  }
  try {
    const dataUrl = await toPng(element, {
      pixelRatio: 2,
      cacheBust: true,
      backgroundColor: '#ffffff',
    });
    const link = document.createElement('a');
    link.download = `${filename}.png`;
    link.href = dataUrl;
    link.click();
  } catch (e) {
    console.error('downloadDomAsPNG failed', e);
  }
}

export const downloadDataAsCSV = (data: any[], filename: string) => {
  if (!data || data.length === 0) {
    console.error('No data to download');
    return;
  }

  // Convert data to CSV format
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        // Handle values that contain commas or quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    )
  ].join('\n');

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
};

export const downloadAllDataAsZip = async (location: string, dataSets: { [key: string]: any[] }) => {
  // This would require a zip library like JSZip
  // For now, we'll download each dataset as a separate CSV
  const promises = Object.entries(dataSets).map(([name, data]) => {
    return new Promise<void>((resolve) => {
      downloadDataAsCSV(data, `${location}_${name}`);
      resolve();
    });
  });

  await Promise.all(promises);
};

export const formatFilename = (location: string, chartType: string, year?: number) => {
  const sanitizedLocation = location.replace(/[^a-zA-Z0-9]/g, '_');
  const sanitizedChartType = chartType.replace(/[^a-zA-Z0-9]/g, '_');
  const yearSuffix = year ? `_${year}` : '';
  return `${sanitizedLocation}_${sanitizedChartType}${yearSuffix}`;
};
